import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Briefcase, Trophy, Clock, ArrowRight, PackageCheck } from 'lucide-react';

/**
 * 3 cartes cliquables : En cours / Gagnés / En retard
 * Filtrent sur la période passée en props (par défaut: tout)
 * Au clic → navigue vers /prospects?view=... avec les filtres
 */
export default function QuickAccessCards({ period }) {
    const navigate = useNavigate();
    const [data, setData] = useState({ en_cours: 0, attente_installation: 0, gagnes: 0, en_retard: 0, threshold_days: 10 });
    const [loading, setLoading] = useState(true);

    const computeRange = (preset) => {
        const now = new Date();
        const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
        const fmt = (d) => d.toISOString().slice(0, 10);
        switch (preset) {
            case 'day':     return { from: fmt(startOfDay(now)), to: fmt(now) };
            case 'week': {
                const day = now.getDay() || 7;
                const monday = new Date(now); monday.setDate(now.getDate() - (day - 1));
                return { from: fmt(startOfDay(monday)), to: fmt(now) };
            }
            case 'month':   return { from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), to: fmt(now) };
            case 'quarter': {
                const q = Math.floor(now.getMonth() / 3);
                return { from: fmt(new Date(now.getFullYear(), q * 3, 1)), to: fmt(now) };
            }
            case 'year':    return { from: fmt(new Date(now.getFullYear(), 0, 1)), to: fmt(now) };
            default:        return { from: '', to: '' };
        }
    };

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const { from, to } = computeRange(period);
                const params = new URLSearchParams();
                if (from) params.append('date_from', from);
                if (to) params.append('date_to', to);
                const res = await api.get(`/dashboard/cards-summary?${params}`);
                setData(res.data);
            } catch (e) {
                console.error('Erreur quick access cards', e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [period]);

    const goTo = (view) => {
        navigate(`/prospects?view=${view}&period=${period || 'all'}`);
    };

    const cards = [
        {
            id: 'en_cours',
            label: 'Dossiers en cours',
            value: data.en_cours,
            icon: Briefcase,
            gradient: 'from-blue-500 to-cyan-500',
            iconBg: 'bg-blue-500/10',
            iconColor: 'text-blue-600',
            description: 'Dossiers actifs à suivre',
            testid: 'card-en-cours',
        },
        {
            id: 'gagnes',
            label: 'Dossiers gagnés',
            value: data.gagnes,
            icon: Trophy,
            gradient: 'from-emerald-500 to-green-500',
            iconBg: 'bg-emerald-500/10',
            iconColor: 'text-emerald-600',
            description: 'Conversions réussies',
            testid: 'card-gagnes',
        },
        {
            id: 'attente_installation',
            label: "En attente d'installation",
            value: data.attente_installation,
            icon: PackageCheck,
            gradient: 'from-indigo-500 to-violet-500',
            iconBg: 'bg-indigo-500/10',
            iconColor: 'text-indigo-600',
            description: 'Devis signé — à faire poser',
            testid: 'card-attente-installation',
        },
        {
            id: 'en_retard',
            label: 'Dossiers en retard',
            value: data.en_retard,
            icon: Clock,
            gradient: 'from-orange-500 to-red-500',
            iconBg: 'bg-orange-500/10',
            iconColor: 'text-orange-600',
            description: `Inactifs > ${data.threshold_days || 10} jours`,
            testid: 'card-en-retard',
            urgent: data.en_retard > 0,
        },
    ];

    if (loading) {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="quick-access-cards">
            {cards.map(c => {
                const Icon = c.icon;
                return (
                    <button
                        key={c.id}
                        type="button"
                        onClick={() => goTo(c.id)}
                        data-testid={c.testid}
                        className={`group text-left relative overflow-hidden rounded-xl border bg-white hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 ${c.urgent ? 'border-orange-300' : 'border-slate-200'}`}
                    >
                        <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${c.gradient}`} />
                        <div className="p-5">
                            <div className="flex items-start justify-between">
                                <div className={`w-11 h-11 rounded-lg ${c.iconBg} flex items-center justify-center`}>
                                    <Icon className={`w-5 h-5 ${c.iconColor}`} />
                                </div>
                                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-600 group-hover:translate-x-1 transition-all" />
                            </div>
                            <div className="mt-4">
                                <div className="text-3xl font-bold text-slate-900 tabular-nums">
                                    {c.value}
                                </div>
                                <div className="text-sm font-medium text-slate-700 mt-0.5">{c.label}</div>
                                <div className="text-xs text-slate-500 mt-1">{c.description}</div>
                            </div>
                            {c.urgent && (
                                <div className="absolute top-3 right-3">
                                    <span className="relative flex h-2.5 w-2.5">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-orange-500"></span>
                                    </span>
                                </div>
                            )}
                        </div>
                    </button>
                );
            })}
        </div>
    );
}
